/**
 * "Pause my availability until {date}" — SPEC §3.7, §9.2.
 *
 * The alternative this replaces is the instructor submitting three empty months and absorbing
 * six reminder emails, then eventually just ignoring our email — which costs us the month
 * they DO come back for.
 *
 * ## Both dates, defaulting to today
 *
 * §3.7 phrases this as "pause until {date}", and the first version asked only for a return
 * date. Luke's case broke it immediately: entering November availability while knowing he is
 * out for three weeks OF November. A break that can only start today cannot say that, and the
 * alternative — leaving those days marked available — is how a class gets built on somebody
 * who is not there.
 *
 * "From" is pre-filled with today, so the common case is still one field: most people report
 * a break when it is already happening.
 *
 * ## It says what it does NOT do
 *
 * The fear this raises is "am I being taken off the roster?" — and leave sits right next to
 * deactivation, which is exactly that. So the copy says access is unaffected and the break
 * ends by itself. Both are true, and neither is obvious from a button that says "pause".
 */

import { useState } from 'react';
import { formatDate, parseISO } from '@gearup/shared';
import { ApiError, api, type LeaveView } from '../lib/api.js';

/**
 * Today, as the browser sees it, for a `<input type="date">` default.
 *
 * Local getters on purpose: this is the date on the WALL where the instructor is standing,
 * which is what they mean by "from today". The server re-derives it in their home location's
 * zone anyway, so this only has to be a sensible default rather than authoritative.
 */
function todayISO(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
}

export function LeaveNotice({
  leaves,
  onChange,
}: {
  /** Every live break, soonest first. More than one is allowed as long as they do not overlap. */
  leaves: LeaveView[];
  onChange: () => void | Promise<void>;
}) {
  const [opening, setOpening] = useState(false);
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Which break is having its end date moved, and to when. */
  const [moving, setMoving] = useState<string | null>(null);
  const [movedTo, setMovedTo] = useState('');

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await onChange();
      setOpening(false);
      setStartDate(todayISO());
      setEndDate('');
      setReason('');
      setMoving(null);
      setMovedTo('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  // Already away, or about to be. Nothing to ask for — just say what is recorded and offer
  // the way out, since §3.7 requires they can end it early.
  if (leaves.length > 0) {
    const sorted = [...leaves].sort((a, b) => a.startDate.localeCompare(b.startDate));
    return (
      <div className="notice notice--info" style={{ alignItems: 'flex-start' }}>
        <span className="notice__icon" aria-hidden="true">✓</span>
        <span className="grow">
          {sorted.map((l, i) => (
            <span key={l.id} style={{ display: 'block', marginBottom: 8 }}>
              {l.active ? (
                <>
                  You are on a break until <strong>{formatDate(parseISO(l.endDate))}</strong>.
                </>
              ) : (
                <>
                  A break is booked from <strong>{formatDate(parseISO(l.startDate))}</strong> to{' '}
                  <strong>{formatDate(parseISO(l.endDate))}</strong>.
                </>
              )}
              <br />
              <span className="small muted">
                {/* Says what the button does, because the two cases differ and the
                    difference matters: one is "I am back now", the other is "this never
                    happens". */}
                {l.active
                  ? 'Ending it early marks you available from today. The days before today stay as they were.'
                  : 'Canceling removes it completely, as though it had never been booked.'}
              </span>
              <br />
              <span className="row" style={{ marginTop: 6, flexWrap: 'wrap' }}>
                <button
                  className="btn btn--secondary"
                  disabled={busy}
                  onClick={() => run(() => api.leave.end(l.id))}
                >
                  {busy ? 'Saving…' : l.active ? 'I am back early' : 'Cancel this break'}
                </button>
                {/* The middle answer. "Back today" and "cancel it" were the only two, and
                    the commonest thing to say is neither: back on the 20th rather than the
                    30th, known in advance. Extends as well, since that is the same edit. */}
                {moving !== l.id && (
                  <button
                    className="btn btn--ghost"
                    disabled={busy}
                    onClick={() => {
                      setMoving(l.id);
                      setMovedTo(l.endDate);
                    }}
                  >
                    Change the date I am back
                  </button>
                )}
              </span>
              {moving === l.id && (
                <span className="row" style={{ marginTop: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <label className="field" style={{ maxWidth: 190 }}>
                    <span className="field__label">Back on</span>
                    <input
                      className="input"
                      type="date"
                      value={movedTo}
                      min={l.startDate > todayISO() ? l.startDate : todayISO()}
                      onChange={(e) => setMovedTo(e.target.value)}
                    />
                  </label>
                  <button
                    className="btn btn--primary"
                    disabled={busy || !movedTo || movedTo === l.endDate}
                    onClick={() => run(() => api.leave.reschedule(l.id, movedTo))}
                  >
                    Save
                  </button>
                  <button
                    className="btn btn--secondary"
                    disabled={busy}
                    onClick={() => {
                      setMoving(null);
                      setMovedTo('');
                    }}
                  >
                    Cancel
                  </button>
                </span>
              )}
              {i < sorted.length - 1 && <hr style={{ margin: '12px 0 0', opacity: 0.3 }} />}
            </span>
          ))}

          {error && (
            <span className="small" role="alert">{error}</span>
          )}

          {/* A second break is allowed as long as it does not overlap — somebody can be away
              in September and again in December, and having to wait until the first is over
              to say so is how the second one gets forgotten. */}
          {!opening ? (
            <button
              type="button"
              className="btn btn--ghost"
              style={{ padding: 0, minHeight: 'auto' }}
              onClick={() => setOpening(true)}
            >
              Book another break
            </button>
          ) : (
            <BreakForm
              startDate={startDate}
              endDate={endDate}
              reason={reason}
              busy={busy}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              setReason={setReason}
              onCancel={() => {
                setOpening(false);
                setError(null);
              }}
              onSubmit={() =>
                run(() => api.leave.start({ startDate, endDate, reason: reason.trim() || null }))
              }
            />
          )}
        </span>
      </div>
    );
  }

  if (!opening) {
    return (
      <p className="small muted center" style={{ margin: 0 }}>
        Away for a while?{' '}
        <button
          type="button"
          className="btn btn--ghost"
          style={{ padding: 0, minHeight: 'auto' }}
          onClick={() => setOpening(true)}
        >
          Pause my availability
        </button>
      </p>
    );
  }

  return (
    <BreakForm
      heading
      startDate={startDate}
      endDate={endDate}
      reason={reason}
      busy={busy}
      error={error}
      setStartDate={setStartDate}
      setEndDate={setEndDate}
      setReason={setReason}
      onCancel={() => {
        setOpening(false);
        setError(null);
      }}
      onSubmit={() =>
        run(() => api.leave.start({ startDate, endDate, reason: reason.trim() || null }))
      }
    />
  );
}

/**
 * The date pair, shared by "pause my availability" and "book another break".
 *
 * One component because the second one is the same question: two forms would let the
 * validation hints, the min/max pinning and the copy drift apart, and the second form is
 * the one nobody would look at again.
 */
function BreakForm({
  heading = false,
  startDate,
  endDate,
  reason,
  busy,
  error,
  setStartDate,
  setEndDate,
  setReason,
  onCancel,
  onSubmit,
}: {
  heading?: boolean;
  startDate: string;
  endDate: string;
  reason: string;
  busy: boolean;
  error?: string | null;
  setStartDate: (v: string) => void;
  setEndDate: (v: string) => void;
  setReason: (v: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <form
      className={heading ? 'card stack' : 'stack'}
      style={heading ? undefined : { marginTop: 8 }}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      {heading && (
        <>
          <h2 style={{ margin: 0, fontSize: 'var(--text-lg)' }}>Pause my availability</h2>
          <p className="small muted" style={{ margin: 0 }}>
            {/* The reassurance is the point. Leave sits next to deactivation in meaning, and
                "pause" without this line reads as "take me off the roster". "Straight away"
                answers the question Luke asked: this is not a request waiting for approval. */}
            This takes effect straight away, no approval needed. Those days stop counting as
            cover, so no class is built on them. You keep your access, nothing you have
            already sent is lost, and you can come back early.
          </p>
        </>
      )}

      <div className="row row--wrap" style={{ gap: 'var(--space-2)' }}>
        <label className="field grow">
          <span className="field__label">From</span>
          <input
            className="input"
            type="date"
            value={startDate}
            max={endDate || undefined}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
        </label>
        <label className="field grow">
          <span className="field__label">Until you are back</span>
          <input
            className="input"
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => setEndDate(e.target.value)}
            required
          />
        </label>
      </div>

      <label className="field">
        <span className="field__label">Anything we should know? (optional)</span>
        <input
          className="input"
          value={reason}
          placeholder="Traveling, family, studying"
          onChange={(e) => setReason(e.target.value)}
        />
      </label>

      {error && <p className="small" role="alert" style={{ margin: 0 }}>{error}</p>}

      <div className="row">
        <button className="btn btn--primary" type="submit" disabled={busy || !endDate || !startDate}>
          {busy ? 'Saving…' : 'Pause for those dates'}
        </button>
        <button className="btn btn--secondary" type="button" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
